import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { img, imgVariant, getProducts, cleanSlug } from "@/api";
import Seo from "@/components/Seo";
import ReviewsSlider from "@/components/ReviewsSlider";
import ManufacturingSlider from "@/components/ManufacturingSlider";
import GallerySection from "@/components/GallerySection";
import { youTubeEmbed } from "@/lib/youtube";
import {
  ChevronLeft, ChevronRight, ArrowRight, Play, Award, Trophy, Building2, CheckCircle2,
  Zap, Settings2, Headphones, Globe2, Star, Calendar, Images,
} from "lucide-react";

const ICONS = { Zap, Settings2, Headphones, Globe2, Award };

export default function Home() {
  const site = useSite();
  const { open } = useEnquiry();
  const slides = (site && site.hero_slides) || [];
  const home = (site && site.home) || {};
  const categories = (site && site.categories) || [];
  const awards = (site && site.awards) || [];
  const clients = (site && site.clients) || [];
  const news = (site && site.news) || [];
  const testimonials = (site && site.testimonials) || [];
  const gallery = (site && site.gallery) || [];
  const about = (site && site.about) || {};
  const assemblyItems = (Array.isArray(about.assembly) && about.assembly.length > 0)
    ? about.assembly
    : ((about.assembly_images || []).map((im) => ({ image: im, title: "", description: "" })));
  const [idx, setIdx] = useState(0);
  const touchX = React.useRef(0);
  const dragX = React.useRef(null);
  const content = slides[0] || {};

  // Hero auto-advance disabled — slides change only via arrows / dots.

  // Reserve a full-viewport skeleton so the Footer (and below-fold content) stays
  // OFF-SCREEN during the loading phase. Previously the 688px skeleton left the
  // footer visible at ~y763, which then jumped 760px+ when real content mounted —
  // the dominant desktop CLS culprit (measured ~0.91).
  if (!site || site.loading) return <div style={{ minHeight: "100vh" }} data-testid="home-hero-skeleton" />;
  const slide = slides[idx] || {};
  const go = (d) => setIdx((p) => (p + d + slides.length) % slides.length);

  return (
    <div>
      <Seo />

      {/* HERO — one fixed background photo + floating transparent product cut-out per slide */}
      <section className="relative overflow-hidden ekta-hero-tech ekta-hero-rail ekta-corners" data-testid="home-hero"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - touchX.current; if (dx < -64) go(1); else if (dx > 64) go(-1); }}
        onMouseDown={(e) => { dragX.current = e.clientX; }}
        onMouseUp={(e) => { if (dragX.current == null) return; const dx = e.clientX - dragX.current; dragX.current = null; if (dx < -64) go(1); else if (dx > 64) go(-1); }}
        style={{ background: home.hero_bg_color || "#303257", cursor: "grab", minHeight: "clamp(520px, 86vh, 820px)" }}>
        {/* Single common background image (stays fixed across slides), clearly visible */}
        <img src={imgVariant(home.hero_background || slide.image, 1280)} alt="" aria-hidden="true" loading="eager" decoding="async" width="1280" height="820" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.55 }} />
        {/* Controlled colour treatment: darker on the left for text, lighter on the right so the photo + machine read clearly */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(48,50,87,0.94) 0%, rgba(48,50,87,0.80) 38%, rgba(48,50,87,0.42) 68%, rgba(48,50,87,0.20) 100%)" }} />
        <div className="ekta-container relative grid lg:grid-cols-12 gap-6 lg:gap-8 items-center py-10 sm:py-14 lg:py-24 pb-16 lg:pb-28">
          <div className="lg:col-span-6 text-white z-10">
            <div className="ekta-eyebrow mb-4" style={{ color: "var(--yellow)" }}>{content.eyebrow}</div>
            <h1 className="font-extrabold" style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(2rem, 5.5vw, 3.6rem)", lineHeight: 1.12, letterSpacing: "-0.01em", textShadow: "0 2px 20px rgba(0,0,0,0.35)", textAlign: "left", textWrap: "balance" }}>
              {(content.title || "").split("\n").map((t, i) => <span key={i} className="block">{t}</span>)}
            </h1>
            <p className="mt-5 sm:mt-6 text-base lg:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.88)", maxWidth: 540, textShadow: "0 1px 10px rgba(0,0,0,0.3)", textAlign: "justify" }}>{content.subtitle}</p>
            <div className="mt-7 sm:mt-9 flex flex-wrap gap-3">
              {content.cta_primary && <Link to={content.cta_primary_link || "/machines"} className="ekta-btn ekta-btn-yellow" data-testid="hero-cta-primary">{content.cta_primary} <ArrowRight size={16} /></Link>}
              <button onClick={() => open({ contactMode: true })} className="ekta-btn ekta-btn-ghost-light" data-testid="hero-cta-secondary">{content.cta_secondary || "Get in Touch"}</button>
            </div>
          </div>
          <div className="lg:col-span-6 relative z-10 flex items-center justify-center">
            {/* Floating transparent machine cut-out — NO box, NO border, NO background */}
            <img key={slide.image} src={imgVariant(slide.image, 720)} alt={slide.eyebrow || "Ekta machine"} fetchpriority="high" loading="eager" decoding="async" className="hero-slide-in w-full max-w-[640px] h-[220px] sm:h-[320px] lg:h-[440px] object-contain" style={{ filter: "drop-shadow(0 26px 30px rgba(0,0,0,0.45))" }} data-testid="hero-product-image" />
            {slides.length > 1 && <>
              <button aria-label="Previous" onClick={() => go(-1)} className="absolute left-0 sm:left-1 lg:-left-6 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 lg:h-11 lg:w-11 items-center justify-center rounded-full" style={{ background: "rgba(255,255,255,0.16)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }} data-testid="hero-prev"><ChevronLeft size={20} /></button>
              <button aria-label="Next" onClick={() => go(1)} className="absolute right-0 sm:right-1 lg:-right-6 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 lg:h-11 lg:w-11 items-center justify-center rounded-full" style={{ background: "rgba(255,255,255,0.16)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }} data-testid="hero-next"><ChevronRight size={20} /></button>
            </>}
          </div>
        </div>
        {/* Premium industrial bottom chamfer — cuts cleanly into the next section */}
        <div className="absolute bottom-0 left-0 right-0 z-[2]" style={{ height: "clamp(32px, 6vw, 68px)", background: "var(--bg)", clipPath: "polygon(0 100%, 100% 100%, 100% 42%, 0 100%)" }} />
        {/* Prefetch other slides at small size + lazily so switching stays quick without blocking LCP */}
        <div aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }}>
          {slides.map((s, i) => (i !== idx && s.image ? <img key={i} src={imgVariant(s.image, 720)} alt="" loading="lazy" decoding="async" /> : null))}
        </div>
      </section>


      {/* COMPANY */}
      <section className="ekta-section bg-white" id="about">
        <div className="ekta-container grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
          <div className="relative overflow-hidden rounded-lg border order-1" style={{ borderColor: "var(--border-c)" }}>
            {(() => {
              const embed = youTubeEmbed(home.company_video_url);
              if (embed) return <div className="relative w-full" style={{ paddingTop: "56.25%", background: "#000" }}><iframe title={home.company_heading || "Company video"} src={embed} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen frameBorder="0" data-testid="home-about-video" /></div>;
              if (home.company_video_url) return <video src={img(home.company_video_url)} poster={img(home.company_video_thumb) || undefined} controls playsInline className="w-full h-[240px] sm:h-[320px] object-cover bg-black" data-testid="home-about-video" />;
              return <img src={img(home.company_video_thumb)} alt={home.company_heading} className="w-full h-[240px] sm:h-[320px] object-cover" />;
            })()}
          </div>
          <div className="order-2" style={{ textAlign: home.company_align || "left" }}>
            <h2 className="ekta-h2">{home.company_heading}</h2>
            <div className={`mt-4 prose-ekta ekta-journey-text align-${home.company_align || "justify"}`} style={{ "--cfs": `${Number(home.company_font_size) || 16}px`, "--cfs-m": `${Number(home.company_font_size_mobile) || Number(home.company_font_size) || 15}px` }}>{(home.company_text || []).map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}</div>
            <div className="flex mt-3" style={{ justifyContent: home.company_align === "center" ? "center" : home.company_align === "right" ? "flex-end" : "flex-start" }}><Link to="/about" className="ekta-btn ekta-btn-outline">About Us <ArrowRight size={16} /></Link></div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE BADGE */}
      {home.experience_badge && (
        <div className="relative bg-white">
          <div className="ekta-container">
            <div className="relative flex justify-center pb-2">
              <div className="absolute left-0 right-0 top-1/2 h-[3px]" style={{ background: "var(--yellow)" }} />
              <div className="relative z-10 inline-flex items-center gap-3 rounded-md border-2 bg-white px-6 py-3.5" style={{ borderColor: "var(--yellow)" }}>
                <Award size={22} style={{ color: "var(--brand-primary)" }} />
                <span className="font-bold tracking-wide text-center" style={{ color: "var(--brand-primary)", fontFamily: "var(--font-heading)", fontSize: 15 }}>{home.experience_badge}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SHOWCASE CATEGORIES */}
      <section className="ekta-section ekta-diagonal" id="machines" style={{ background: "var(--bg-2)", borderTop: "3px solid var(--yellow)", borderBottom: "3px solid var(--yellow)" }}>
        <div className="ekta-container">
          <div className="text-center max-w-3xl mx-auto mb-9">
            <h2 className="ekta-h2">{home.showcase_heading}</h2>
            <p className="mt-3" style={{ color: "var(--muted-ink)" }}>{home.showcase_subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((c) => (
              <Link key={c.id} to={`/machines/${c.slug}`} className="group relative overflow-hidden rounded-lg" style={{ height: 260 }} data-testid="home-category-card">
                <img src={img(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(33,33,70,0.15) 0%, rgba(14,15,20,0.82) 100%)" }} />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-bold text-white uppercase tracking-wide" style={{ fontSize: 19, fontFamily: "var(--font-heading)" }}>{c.name}</h3>
                  <div className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-white"><span style={{ color: "var(--yellow)" }}>View Products</span> <ArrowRight size={15} style={{ color: "var(--yellow)" }} /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MANUFACTURING ASSEMBLY — static grid (no horizontal slider) */}

      {/* MANUFACTURING ASSEMBLY — horizontal slider */}
      <ManufacturingSlider
        items={assemblyItems}
        eyebrow="Inside Our Factory"
        heading={home.assembly_heading || "Manufacturing & Assembly"}
        subtitle={home.assembly_subtitle || "A look inside the Ekta plant — precision machining, disciplined assembly and rigorous testing behind every machine we ship."}
        background="var(--bg-2)"
      />

      {/* AWARDS */}
      <section className="ekta-section bg-white" style={{ paddingTop: 0 }}>
        <div className="ekta-container">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div><div className="ekta-eyebrow" style={{ color: "var(--yellow-dark)" }}>Excellence &amp; Recognition</div><h2 className="ekta-h2 mt-2">Achievements &amp; Awards</h2></div>
            <Link to="/awards" className="ekta-btn ekta-btn-outline shrink-0" data-testid="home-view-awards">View All Awards <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {awards.slice(0, 4).map((a, i) => (
              <div key={a.id || i} className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--border-c)" }} data-testid="home-award-card">
                <div className="relative flex items-center justify-center" style={{ height: 190, background: "linear-gradient(160deg,#24244f,#16163a)" }}>
                  {img(a.image) ? <img src={img(a.image)} alt={a.title || a.caption} className="w-full h-full object-cover" loading="lazy" /> : <Trophy size={44} style={{ color: "var(--yellow)" }} strokeWidth={1.4} />}
                </div>
                <div className="p-4"><p className="text-sm" style={{ color: "var(--muted-ink)" }}>{a.title || a.caption}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="ekta-section" style={{ background: "var(--bg-2)" }}>
        <div className="ekta-container">
          <div className="text-center max-w-2xl mx-auto mb-8"><div className="ekta-eyebrow" style={{ color: "var(--yellow-dark)" }}>Trusted Worldwide</div><h2 className="ekta-h2 mt-2">Our Clients</h2><p className="mt-3" style={{ color: "var(--muted-ink)" }}>Serving 1500+ manufacturers across 25+ countries in Asia, Africa and the USA.</p></div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            {clients.slice(0, 18).map((c, i) => (
              <div key={c.id || i} className="flex items-center justify-center rounded-lg border bg-white" style={{ height: 92, borderColor: "var(--border-c)" }} data-testid="home-client-logo">
                {img(c.logo) ? <img src={img(c.logo)} alt={c.name || "Client"} className="max-h-[64px] max-w-[80%] object-contain" loading="lazy" /> : <Building2 size={30} style={{ color: "var(--border-2)" }} />}
              </div>
            ))}
          </div>
          <div className="text-center mt-9"><Link to="/clients" className="ekta-btn ekta-btn-primary" data-testid="home-view-clients">View All Clients <ArrowRight size={16} /></Link></div>
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section className="ekta-section bg-white" id="news">
          <div className="ekta-container">
            <div className="flex items-center justify-between border-b pb-5 mb-8" style={{ borderColor: "var(--border-c)" }}>
              <h2 className="ekta-h2">News &amp; Updates</h2>
              <Link to="/news" className="text-sm font-bold uppercase tracking-wide inline-flex items-center gap-1" style={{ color: "var(--brand-primary)" }}>All News <ArrowRight size={15} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.slice(0, 3).map((n) => (
                <Link key={n.id} to={`/news/${cleanSlug(n.slug)}`} className="group rounded-lg border bg-white overflow-hidden hover:shadow-[var(--shadow-md)] transition-shadow" style={{ borderColor: "var(--border-c)" }} data-testid="home-news-card">
                  <div className="relative"><img src={img(n.image)} alt={n.title} className="w-full h-[190px] object-cover" loading="lazy" /><div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-md px-2.5 py-1.5 bg-white/95"><Calendar size={13} style={{ color: "var(--brand-primary)" }} /><span className="text-xs font-bold" style={{ color: "var(--brand-primary)" }}>{n.date}</span></div>{(n.images || []).length > 0 && (<div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-white" style={{ background: "rgba(33,33,70,0.9)" }} data-testid="home-news-photocount"><Images size={13} style={{ color: "var(--yellow)" }} /><span className="text-xs font-bold">{(n.images || []).length + 1} Photos</span></div>)}</div>
                  <div className="p-6"><span className="ekta-eyebrow" style={{ color: "var(--yellow-dark)" }}>{n.category}</span><h3 className="font-bold leading-snug mt-2" style={{ fontSize: 16, color: "var(--ink)" }}>{n.title}</h3><p className="mt-2 text-sm" style={{ color: "var(--muted-ink)" }}>{n.excerpt}</p></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS */}
      {(!site.site || !site.site.reviews || site.site.reviews.enabled !== false) && (
        <ReviewsSlider testimonials={testimonials} fallbackPlaceUrl={(site.site && site.site.reviews && site.site.reviews.place_url) || ""} />
      )}

      {/* WHY CHOOSE */}
      <section className="relative ekta-diagonal-dark text-white" style={{ background: "var(--brand-primary)", borderTop: "3px solid var(--yellow)" }} id="why">
        <div className="ekta-container grid lg:grid-cols-2 gap-10 items-center ekta-section">
          <div className="relative overflow-hidden rounded-lg order-2 lg:order-1" style={{ boxShadow: "0 30px 60px rgba(0,0,0,0.35)" }}><img src={img(home.why_image)} alt="Ekta facility" className="w-full h-[280px] lg:h-[400px] object-cover" loading="lazy" /></div>
          <div className="order-1 lg:order-2">
            <div className="ekta-eyebrow mb-3" style={{ color: "var(--yellow)" }}>Why Choose Ekta</div>
            <h2 className="font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(1.8rem,3vw,2.6rem)" }}>{home.why_heading || "Why Choose Us?"}</h2>
            <p className="mt-4" style={{ color: "rgba(255,255,255,0.75)", maxWidth: 520 }}>{home.why_intro}</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {(home.why_points || []).map((p, i) => (
                <div key={i} className="flex gap-3"><CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--yellow)" }} /><span className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.88)" }}>{p}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
